// Every "N things need your attention" count in the app, declared once.
//
// See docs/UI-CONVENTIONS.md for the rule this enforces. The short version:
// a queue count appears on its sidebar module and on the tab that owns it,
// in one tone, from one number.
//
// It used to be per-feature improvisation. Bug reports got a sidebar badge and
// no tab badge; leave got a tab badge, a KPI tile, a pill, a 44px numeral and no
// sidebar badge; the bell and the Inbox each counted unread over a different
// page window and disagreed. Declaring the queue here instead of at each render
// site is what stops that happening again — both surfaces read the same entry.
import { useCallback, useEffect, useState } from 'react';
import { supabase } from './supabase';
import { can, type PermLevel, type PermMap } from './roles';

export type QueueId =
  | 'leave'
  | 'bugs'
  | 'notifications'
  | 'corrections'
  | 'day_edits'
  | 'comp_hours'
  | 'key_dates'
  | 'policies';

export type AttentionQueue = {
  id: QueueId;
  /**
   * The sidebar module this rolls up to. A role_permissions.module key, or
   * `MY_SPACE` for a queue that is the person's own, whose door is the
   * sidebar's account card rather than a module.
   */
  module: string;
  /** The tab that owns this queue, if the screen has tabs. */
  tab?: string;
  /**
   * The permission needed to ACT on the queue — deliberately not the permission
   * needed to see the screen. hr can open /org (org:edit) but cannot triage a bug
   * report (org:full), so badging Organisation for them would point at a tab they
   * do not have. Gate on doing, never on looking.
   */
  minLevel: PermLevel;
  /** Rows awaiting action. Always head+count: no rows over the wire, and every
   *  surface therefore renders the identical number. `me` is the viewer, for a
   *  queue whose own rows are not theirs to clear. */
  count: (me: string) => PromiseLike<number>;
};

/**
 * One counting shape for all of them: `head: true` so PostgREST returns the
 * count and no rows. Yields 0 when the app is running without a backend, which
 * is the same not-connected posture the rest of lib/ takes.
 */
const headCount = async (
  build: (sb: NonNullable<typeof supabase>) => PromiseLike<{ count: number | null }>,
): Promise<number> => {
  if (!supabase) return 0;
  const { count } = await build(supabase);
  return count ?? 0;
};

/** The account card's roll-up: every signed-in person acts on their own My space. */
export const MY_SPACE = 'me';

export const QUEUES: readonly AttentionQueue[] = [
  {
    id: 'leave',
    module: 'people',
    tab: 'requests',
    // people:full — owner/admin/hr. A lead is people:view and cannot decide, so
    // no badge. Before this, the tab badge had no gate at all and a member saw a
    // count of their own pending requests dressed up as an approval queue.
    // Not your own either: nobody decides their own leave (20261103100000).
    minLevel: 'full',
    count: (me) =>
      headCount((sb) =>
        sb
          .from('leave_requests')
          .select('id', { head: true, count: 'exact' })
          .eq('status', 'pending')
          .neq('profile_id', me),
      ),
  },
  {
    id: 'corrections',
    module: 'people',
    tab: 'requests',
    // Same tab, same gate, same shape as leave: a correction is someone asking
    // for their record to be changed, so it queues where every other such
    // request does. people:full — owner/admin/hr. A lead holds timesheets:full
    // and can edit hours directly, but deciding a request is not theirs, and
    // tc_read does not show them one. A member sees their own request's status
    // inline on Timesheets, which is object status, not a queue. Your own is
    // another approver's (enforce_time_correction).
    minLevel: 'full',
    count: (me) =>
      headCount((sb) =>
        sb
          .from('time_corrections')
          .select('id', { head: true, count: 'exact' })
          .eq('status', 'pending')
          .neq('profile_id', me),
      ),
  },
  {
    id: 'day_edits',
    module: 'people',
    tab: 'requests',
    // Edit day (20261103120000): a whole-day change someone asked for. HR or
    // the direct manager decides it; the badge is HR's, like corrections — a
    // manager is reached by the notice and the card on their own /me. Your
    // own is another approver's (decide_day_edit).
    minLevel: 'full',
    count: (me) =>
      headCount((sb) =>
        sb
          .from('day_edits')
          .select('id', { head: true, count: 'exact' })
          .eq('status', 'pending')
          .neq('profile_id', me),
      ),
  },
  {
    id: 'comp_hours',
    module: 'people',
    tab: 'requests',
    // people:full, but this badge counts only the claims that are actually HR's
    // to decide — the ones whose owner has no reporting manager on file. A claim
    // belonging to a live manager is waiting on THEM, and badging it here would
    // point HR at a queue that is not theirs and that they can only clear by
    // filing an exception. Managers are reached by notification and by the card
    // on their own /me, which is a surface the registry has no module for.
    // Your own claim is someone else's to decide, manager or not.
    minLevel: 'full',
    count: (me) =>
      headCount((sb) =>
        sb
          .from('compensated_hours')
          .select('id, profiles!compensated_hours_profile_id_fkey!inner(manager_id)', {
            head: true,
            count: 'exact',
          })
          .eq('status', 'pending')
          .is('profiles.manager_id', null)
          .neq('profile_id', me),
      ),
  },
  {
    id: 'key_dates',
    module: 'people',
    tab: 'requests',
    // Only LAPSED dates. A date still ahead of its reminder is not a to-do, and
    // R6.3 asks specifically for the ones that passed unactioned to surface —
    // that is the whole "silence is visible" mechanism. Upcoming ones are
    // reminded by the nightly sweep instead.
    minLevel: 'full',
    count: () =>
      headCount((sb) =>
        sb.from('key_date').select('id', { head: true, count: 'exact' }).eq('status', 'lapsed'),
      ),
  },
  {
    id: 'bugs',
    module: 'org',
    tab: 'bugs',
    // org:full — owner/admin only. hr sits at org:edit and is deliberately
    // outside triage (20260805110000_bug_reports.sql).
    minLevel: 'full',
    count: () =>
      headCount((sb) =>
        sb.from('bug_reports').select('id', { head: true, count: 'exact' }).is('resolved_at', null),
      ),
  },
  {
    id: 'policies',
    module: MY_SPACE,
    // Policies addressed to you that you have not acknowledged (FR-8.7). Only
    // you can clear them, so it is yours whatever your permissions; my_policies
    // is RLS-scoped to the caller and returns nothing to a client.
    minLevel: 'view',
    count: () =>
      headCount((sb) =>
        sb
          .from('my_policies')
          .select('id', { head: true, count: 'exact' })
          .is('acknowledged_at', null),
      ),
  },
  {
    id: 'notifications',
    // Stays `inbox`: it must be a role_permissions key. The nav item it badges
    // is Workspace, which rolls up both of its modules.
    module: 'inbox',
    tab: 'notifications',
    // Yours by definition — RLS scopes notifications to the recipient, so
    // anyone who can open the Notifications tab can act on what is in it.
    minLevel: 'view',
    count: () =>
      headCount((sb) =>
        sb.from('notifications').select('id', { head: true, count: 'exact' }).is('read_at', null),
      ),
  },
];

/** The queues this permission map may act on. Pure — this is what the test pins. */
export const visibleQueues = (perms: PermMap | undefined): AttentionQueue[] =>
  QUEUES.filter((q) =>
    // My space has no permission; it waits, like every queue, for them to load.
    q.module === MY_SPACE
      ? !!perms && Object.keys(perms).length > 0
      : can(perms, q.module, q.minLevel),
  );

export type AttentionCounts = {
  /** Roll-up per module, for the sidebar. */
  byModule: Record<string, number>;
  /** Per queue, for the tab that owns it. */
  byQueue: Partial<Record<QueueId, number>>;
  reload: () => void;
};

/**
 * Runs only the queues this user may act on, and re-runs on navigation — which
 * is frequent enough to stay fresh without holding a realtime channel open for
 * a number. `deps` is usually the current screen. `me` is the viewer's id.
 */
export function useAttention(
  perms: PermMap | undefined,
  me: string | undefined,
  dep: unknown,
): AttentionCounts {
  const [byQueue, setByQueue] = useState<Partial<Record<QueueId, number>>>({});

  const load = useCallback(async () => {
    // RLS answers a signed-out read with nothing, so no viewer means no count.
    if (!supabase || !me) return;
    const qs = visibleQueues(perms);
    // One failing queue must not blank the others — a badge that silently
    // vanishes reads as "nothing to do", which is the worst possible failure
    // mode for this particular widget.
    const counts = await Promise.all(
      qs.map(async (q) => {
        try {
          return [q.id, await q.count(me)] as const;
        } catch {
          return [q.id, 0] as const;
        }
      }),
    );
    setByQueue(Object.fromEntries(counts) as Partial<Record<QueueId, number>>);
  }, [perms, me]);

  useEffect(() => {
    void load();
  }, [load, dep]);

  const byModule: Record<string, number> = {};
  for (const q of visibleQueues(perms)) {
    byModule[q.module] = (byModule[q.module] ?? 0) + (byQueue[q.id] ?? 0);
  }

  // `load` itself, not a wrapper: a caller can depend on reload without it
  // changing every render.
  return { byModule, byQueue, reload: load };
}
